"use client";
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from "react";
import { ChevronDown, Search, RefreshCw } from "lucide-react";
import { Button } from "../ui/button.js";
import { useVirtualizer } from "@tanstack/react-virtual";
import { cn } from "../../lib/utils.js";
import { useDropdownListScroll } from "../../lib/dropdown-wheel-scroll.js";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger, } from "../ui/dropdown-menu.js";
import { Input } from "../ui/input.js";
import { Checkbox } from "../ui/checkbox.js";
import { Separator } from "../ui/separator.js";
export function FilterDropdown(_a) {
    var items = _a.items, selectedItems = _a.selectedItems, onSelectionChange = _a.onSelectionChange, onApply = _a.onApply, onReset = _a.onReset, _b = _a.name, name = _b === void 0 ? "Filter" : _b, _c = _a.placeholder, placeholder = _c === void 0 ? "Search" : _c, triggerLabel = _a.triggerLabel, className = _a.className, headerContent = _a.headerContent, _d = _a.virtualizationThreshold, virtualizationThreshold = _d === void 0 ? 100 : _d, _e = _a.itemHeight, itemHeight = _e === void 0 ? 40 : _e, _f = _a.maxHeight, maxHeight = _f === void 0 ? 240 : _f, _g = _a.showActions, showActions = _g === void 0 ? true : _g, _h = _a.applyText, applyText = _h === void 0 ? "Apply" : _h, _j = _a.resetText, resetText = _j === void 0 ? "Reset" : _j, _k = _a.closeOnApply, closeOnApply = _k === void 0 ? true : _k, _l = _a.closeOnReset, closeOnReset = _l === void 0 ? false : _l, classNameOptions = _a.classNameOptions, _m = _a.buttonMode, buttonMode = _m === void 0 ? false : _m, _o = _a.buttonType, buttonType = _o === void 0 ? "outline" : _o, _p = _a.singleSelect, singleSelect = _p === void 0 ? false : _p;
    var _q = React.useState(false), open = _q[0], setOpen = _q[1];
    var _r = React.useState(""), searchQuery = _r[0], setSearchQuery = _r[1];
    var _s = React.useState([]), tempSelected = _s[0], setTempSelected = _s[1];
    // Check if we're inside a sheet/modal context
    var containerRef = React.useRef(null);
    var isInsideSheet = React.useMemo(function () {
        if (typeof window === 'undefined')
            return false;
        var checkForSheet = function () {
            var element = containerRef.current;
            if (!element)
                return false;
            return !!(element.closest('[role="dialog"]') ||
                element.closest('[data-state="open"]') ||
                element.closest('.sheet-content') ||
                element.closest('[data-radix-sheet-content]'));
        };
        return checkForSheet();
    }, []);
    // Sync temp selection with selectedItems when dropdown opens
    React.useEffect(function () {
        // if (open) {
        setTempSelected(selectedItems);
        // }
    }, [open, selectedItems]);
    var filteredItems = React.useMemo(function () {
        return items.filter(function (item) {
            return item.label.toLowerCase().includes(searchQuery.toLowerCase());
        });
    }, [items, searchQuery]);
    // Virtualization setup
    var listRef = React.useRef(null);
    var _t = React.useState(null), listNode = _t[0], setListNode = _t[1];
    var shouldVirtualize = filteredItems.length >= virtualizationThreshold;
    var virtualizer = useVirtualizer({
        count: filteredItems.length,
        getScrollElement: React.useCallback(function () { return listRef.current; }, []),
        estimateSize: React.useCallback(function () { return itemHeight; }, [itemHeight]),
        overscan: 5,
    });
    // Ensure measurements are up-to-date when the dropdown opens
    React.useEffect(function () {
        if (open) {
            virtualizer.measure();
        }
    }, [open, virtualizer]);
    // Callback ref to update listRef & measure
    var setListRef = React.useCallback(function (node) {
        listRef.current = node;
        setListNode(node);
        if (node && open) {
            virtualizer.measure();
        }
    }, [open, virtualizer]);
    useDropdownListScroll(listNode, open);
    var handleSelectAll = function (checked) {
        var newSelection;
        if (singleSelect) {
            // In single select mode, "All" selects the first item
            if (checked && filteredItems.length > 0) {
                newSelection = [filteredItems[0].id];
            }
            else {
                newSelection = [];
            }
        }
        else {
            if (checked) {
                newSelection = __spreadArray(__spreadArray([], tempSelected, true), filteredItems.map(function (item) { return item.id; }), true);
                // remove duplicates
                newSelection = Array.from(new Set(newSelection));
            }
            else {
                newSelection = tempSelected.filter(function (id) { return !filteredItems.some(function (item) { return item.id === id; }); });
            }
        }
        // Check if the selection is allowed
        var isAllowed = onSelectionChange(newSelection);
        if (isAllowed !== false) {
            setTempSelected(newSelection);
        }
    };
    var handleItemSelect = function (itemId, checked) {
        var newSelection;
        if (singleSelect) {
            newSelection = [itemId];
        }
        else {
            if (checked) {
                newSelection = __spreadArray(__spreadArray([], tempSelected, true), [itemId], false);
            }
            else {
                newSelection = tempSelected.filter(function (id) { return id !== itemId; });
            }
        }
        // Check if the selection is allowed
        var isAllowed = onSelectionChange(newSelection);
        if (isAllowed !== false) {
            setTempSelected(newSelection);
        }
    };
    var handleOnlySelect = function (itemId) {
        setTempSelected([itemId]);
    };
    var handleApply = function () {
        onSelectionChange(tempSelected);
        if (onApply) {
            onApply(tempSelected);
        }
        if (closeOnApply) {
            setOpen(false);
        }
        setSearchQuery("");
    };
    var handleReset = function () {
        setTempSelected([]);
        if (onReset) {
            onReset();
        }
        if (closeOnReset) {
            setOpen(false);
        }
    };
    var isAllSelected = React.useMemo(function () {
        if (singleSelect) {
            // In single select mode, "All" is selected if any item is selected
            return tempSelected.length > 0;
        }
        return filteredItems.length > 0 && filteredItems.every(function (item) { return tempSelected.includes(item.id); });
    }, [filteredItems, tempSelected, singleSelect]);
    var displayLabel = React.useMemo(function () {
        if (buttonMode) {
            return triggerLabel || name;
        }
        if (tempSelected.length === 0) {
            return triggerLabel || name;
        }
        if (singleSelect) {
            // In single select mode, show the selected item's label
            var selectedItem = items.find(function (item) { return item.id === tempSelected[0]; });
            return selectedItem ? selectedItem.label : triggerLabel || name;
        }
        if (tempSelected.length === items.length) {
            return "All";
        }
        if (tempSelected.length <= 2) {
            return tempSelected.map(function (id) { var _a; return (_a = items.find(function (item) { return item.id === id; })) === null || _a === void 0 ? void 0 : _a.label; }).join(", ");
        }
        return "".concat(tempSelected.length, " selected");
    }, [tempSelected, items, triggerLabel, name, buttonMode, singleSelect]);
    return (_jsx("div", { ref: containerRef, children: _jsxs(DropdownMenu, { open: open, onOpenChange: setOpen, children: [_jsx(DropdownMenuTrigger, { asChild: true, children: _jsxs(Button, { variant: buttonType, role: "combobox", "aria-expanded": open, className: cn(buttonMode
                            ? "h-9 justify-center text-sm font-normal transition-all duration-150 rounded-md"
                            : "h-9 justify-between text-sm font-normal text-foreground border-border hover:bg-muted hover:border-border focus:border-border focus:ring-0 transition-all duration-150 rounded-md min-w-[120px] max-w-[600px]", !buttonMode && open && "border-border ring-0", className), children: [_jsxs("span", { className: cn("truncate flex-1 text-left flex items-center gap-1", buttonMode && "justify-center"), children: [!buttonMode && (_jsxs("span", { className: "font-semibold text-foreground capitalize", children: [name, ":"] })), typeof displayLabel === 'string' ? (_jsx("span", { className: cn("font-normal", buttonMode ? "text-primary-foreground" : "text-foreground"), children: displayLabel })) : (displayLabel)] }), !buttonMode && (_jsx(ChevronDown, { className: cn("ml-2 h-4 w-4 shrink-0 transition-transform duration-200 text-muted-foreground", open && "text-foreground"), style: { transform: open ? 'rotate(180deg)' : 'rotate(0deg)' } }))] }) }), _jsxs(DropdownMenuContent, { className: cn("p-0 bg-background border border-border rounded-lg shadow-[0_2px_8px_0_rgba(0,0,0,0.08)] animate-in fade-in-0 zoom-in-95 min-w-[120px] !overflow-hidden", showActions && "min-w-[240px]", classNameOptions), align: "start", sideOffset: 4, avoidCollisions: true, onInteractOutside: function (event) {
                        if (isInsideSheet) {
                            event.preventDefault();
                            return;
                        }
                        // Allow normal outside click behavior when not in a sheet
                    }, children: [_jsxs("div", { className: "sticky top-0 z-10 bg-background border-b border-border", children: [!singleSelect && (_jsxs("div", { className: "flex items-center gap-2 px-3 py-2 cursor-pointer transition-colors duration-150 hover:bg-muted bg-primary dark:bg-primary-light", onClick: function () { return handleSelectAll(!isAllSelected); }, children: [_jsx(Checkbox, { checked: isAllSelected, className: "h-4 w-4" }), _jsx("span", { className: "text-sm font-medium text-primary-foreground capitalize", children: name })] })), headerContent && (_jsx("div", { className: "px-3 py-2 border-b border-border", children: headerContent })), _jsx("div", { className: "py-1 border-b border-border", children: _jsxs("div", { className: "relative px-3", children: [_jsx(Search, { className: "absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" }), _jsx(Input, { placeholder: placeholder, value: searchQuery, onChange: function (e) { return setSearchQuery(e.target.value); }, className: "h-8 pl-8 text-sm border-0 rounded-none focus-visible:ring-0 focus-visible:border-border", onClick: function (e) { return e.stopPropagation(); }, onKeyDown: function (e) { return e.stopPropagation(); } })] }) })] }), _jsx("div", { ref: setListRef, className: "min-h-0 overflow-y-auto overscroll-contain scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent", style: {
                                height: shouldVirtualize ? maxHeight : Math.min(maxHeight, filteredItems.length * itemHeight),
                                maxHeight: maxHeight,
                                minHeight: filteredItems.length > 0 ? itemHeight : 60,
                            }, children: filteredItems.length === 0 ? (_jsx("div", { className: "text-center text-muted-foreground py-4 text-sm", children: "No options available" })) : shouldVirtualize ? (_jsx("div", { style: {
                                    height: "".concat(virtualizer.getTotalSize(), "px"),
                                    width: "100%",
                                    position: "relative",
                                    minHeight: itemHeight,
                                }, children: virtualizer.getVirtualItems().map(function (virtualItem) {
                                    var item = filteredItems[virtualItem.index];
                                    if (!item)
                                        return null;
                                    return (_jsxs("div", { className: "flex items-center justify-between px-3 py-2 cursor-pointer transition-colors duration-150 group absolute left-0 top-0 hover:bg-muted", style: {
                                            transform: "translateY(".concat(virtualItem.start, "px)"),
                                            height: "".concat(itemHeight, "px"),
                                            width: "100%",
                                        }, onClick: function () {
                                            if (singleSelect) {
                                                // In single select mode, clicking an item selects it and closes the dropdown
                                                setTempSelected([item.id]);
                                                onSelectionChange([item.id]);
                                                if (closeOnApply) {
                                                    setOpen(false);
                                                }
                                                setSearchQuery("");
                                            }
                                            else {
                                                handleItemSelect(item.id, !tempSelected.includes(item.id));
                                            }
                                        }, children: [_jsxs("div", { className: "flex items-center gap-2 flex-1 min-w-0", children: [_jsx(Checkbox, { id: item.id, checked: tempSelected.includes(item.id), onCheckedChange: function (checked) {
                                                            return handleItemSelect(item.id, checked);
                                                        }, className: "h-4 w-4 shrink-0" }), _jsx("span", { className: cn("text-sm font-normal truncate cursor-pointer", tempSelected.includes(item.id) ? "text-foreground" : "text-muted-foreground group-hover:text-foreground"), children: item.label })] }), !singleSelect && (_jsx(Button, { variant: "ghost", size: "sm", className: "opacity-0 group-hover:opacity-100 h-6 px-2 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-transparent", onClick: function (e) {
                                                    e.stopPropagation();
                                                    handleOnlySelect(item.id);
                                                }, children: "Only" }))] }, item.id));
                                }) })) : (filteredItems.map(function (item) { return (_jsxs("div", { className: "flex items-center justify-between px-3 py-2 cursor-pointer transition-colors duration-150 group hover:bg-muted", onClick: function () {
                                    if (singleSelect) {
                                        // In single select mode, clicking an item selects it and closes the dropdown
                                        setTempSelected([item.id]);
                                        onSelectionChange([item.id]);
                                        if (closeOnApply) {
                                            setOpen(false);
                                        }
                                        setSearchQuery("");
                                    }
                                    else {
                                        handleItemSelect(item.id, !tempSelected.includes(item.id));
                                    }
                                }, children: [_jsxs("div", { className: "flex items-center gap-2 flex-1 min-w-0", children: [_jsx(Checkbox, { id: item.id, checked: tempSelected.includes(item.id), onCheckedChange: function (checked) {
                                                    return handleItemSelect(item.id, checked);
                                                }, className: "h-4 w-4 shrink-0" }), _jsx("span", { className: cn("text-sm font-normal truncate cursor-pointer", tempSelected.includes(item.id) ? "text-foreground" : "text-muted-foreground group-hover:text-foreground"), children: item.label })] }), !singleSelect && (_jsx(Button, { variant: "ghost", size: "sm", className: "opacity-0 group-hover:opacity-100 h-6 px-2 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-transparent", onClick: function (e) {
                                            e.stopPropagation();
                                            handleOnlySelect(item.id);
                                        }, children: "Only" }))] }, item.id)); })) }), showActions && (_jsxs(_Fragment, { children: [_jsx(Separator, { className: "bg-border" }), _jsxs("div", { className: "sticky bottom-0 bg-background border-t border-border p-2 flex items-center gap-2 w-full min-w-0", children: [_jsxs(Button, { variant: "outline", size: "sm", onClick: handleReset, disabled: filteredItems.length === 0, className: "h-8 px-2.5 text-xs flex-1 min-w-0", children: [_jsx("span", { className: "truncate", children: resetText }), _jsx(RefreshCw, { className: "ml-1 h-3 w-3 shrink-0" })] }), _jsx(Button, { variant: "default", size: "sm", onClick: handleApply, disabled: filteredItems.length === 0, className: "h-8 px-2.5 text-xs flex-1 min-w-0", children: _jsx("span", { className: "truncate", children: applyText }) })] })] }))] })] }) }));
}
